import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Box, Button, Card, DuoColorIcon, Icon, Tag, Text } from '@zillow/constellation-10';
import { IconDoorOpenFilled } from '@zillow/constellation-icons';

import { getTagTypeByKey, STATUS_KEY } from '../../../../../../utils/statusTagUtils';

import { STEP_KEYS } from '../../../../constants/stepKeys';
import { useWizardNavigation } from '../../../../WizardNavigationProvider';

import type { Route } from 'nextjs-routes';
import type { MouseEvent } from 'react';
import { type MultiplexBuildingSessionOverviewUnitStatusWebDto } from '@zg-rentals/ts-rental-manager-api-client';

export type UnitItemProps = {
  unitName: string;
  unitType?: string;
  status: MultiplexBuildingSessionOverviewUnitStatusWebDto;
};

const stripBasePath = (url: string) => {
  const withoutPrefix = url.trim().replace(/^\/?rental-manager\/?/, '');
  return withoutPrefix.startsWith('/') ? withoutPrefix : `/${withoutPrefix}`;
};

const isCreateListingCta = (url: string) => /\/create-listing/i.test(url.trim());

const UnitItem = ({ unitName, unitType, status }: UnitItemProps) => {
  const router = useRouter();
  const { goToStep } = useWizardNavigation();
  const ctaUrl = status.buttonUrl?.trim() ?? '';
  const linkHref = stripBasePath(ctaUrl) as unknown as Route;
  const hasCtaUrl = Boolean(ctaUrl);
  const [isNavigationPending, setIsNavigationPending] = useState(false);

  useEffect(() => {
    setIsNavigationPending(false);
  }, [ctaUrl]);

  const handleCtaClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>, url: string) => {
      if (isNavigationPending) {
        event.preventDefault();
        return;
      }

      const leaveDefaultBrowserNavigation =
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

      if (leaveDefaultBrowserNavigation) {
        return;
      }

      event.preventDefault();
      setIsNavigationPending(true);

      try {
        // Stay in wizard: sync URL from buttonUrl (listing/query), then open review.
        if (isCreateListingCta(ctaUrl)) {
          await router.push(stripBasePath(url) as unknown as Route);
          goToStep(STEP_KEYS.REVIEW);
          return;
        }

        // Leave wizard (e.g. dashboard/overview).
        await router.push(stripBasePath(url) as unknown as Route);
      } finally {
        setIsNavigationPending(false);
      }
    },
    [ctaUrl, goToStep, isNavigationPending, router],
  );

  return (
    <Card
      data-testid="UnitItem"
      tone="neutral"
      elevated={false}
      css={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '200',
        width: '100%',
        paddingBlock: '300',
        paddingInline: '400',
      }}
    >
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '300',
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box css={{ flexShrink: 0 }} aria-hidden={true}>
          <DuoColorIcon tone="info">
            <Icon size="md" render={<IconDoorOpenFilled />} />
          </DuoColorIcon>
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: '25', minWidth: 0 }}>
          <Text textStyle="body-bold" css={{ color: 'text.neutral' }}>
            {unitName}
          </Text>
          <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
            {unitType}
          </Text>
        </Box>

        <Tag size="sm" tone={getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, status.displayStatus.trim())}>
          {status.displayStatus}
        </Tag>
      </Box>

      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '200',
          flexShrink: 0,
        }}
      >
        {hasCtaUrl ? (
          <Button asChild tone="brand" emphasis="filled" size="sm">
            <Link
              href={linkHref}
              onClick={(event) => void handleCtaClick(event, ctaUrl)}
              aria-busy={isNavigationPending}
            >
              {status.buttonText}
            </Link>
          </Button>
        ) : (
          <Button type="button" tone="brand" emphasis="filled" size="sm" disabled={true}>
            {status.buttonText}
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default UnitItem;
